/**
 * Отображение markdown.
 *
 * FSD: shared/lib
 *
 * Норма: spec/features/source-explorer/notes.md
 *  - R01 — текст заметки Markdown; интерфейс редактирует и **отображает**
 *    markdown. Без разбора разметки пользователь увидел бы её сырым текстом —
 *    это видимая потеря.
 *  - R02 — при отображении содержимое обезвреживается: активное содержимое
 *    (скрипты и т. п.) не исполняется.
 *
 * Защита от внедрения разметки — два независимых рубежа:
 *
 *  1. `html: false` — разбор markdown **не пропускает** сырой HTML: он
 *     экранируется и показывается как текст. Разметка из пользовательского
 *     ввода в вывод не попадает вовсе, а не вычищается постфактум. Это же
 *     соответствует канону: HTML в заметке — не разметка, а текст.
 *  2. `sanitizeHtml` (DOMPurify) — обезвреживание итогового HTML. Рубеж
 *     остаётся, даже если первый когда-нибудь ослабнет: обработчики событий,
 *     `javascript:`-адреса и активные узлы не доживают до документа.
 *
 * Адреса ссылок дополнительно проверяет сам разбор: `javascript:`,
 * `vbscript:`, `file:` и `data:` (кроме растровых картинок) отклоняются до
 * обезвреживания.
 */

import MarkdownIt from "markdown-it";
import { sanitizeHtml } from "./sanitize";

const renderer = new MarkdownIt({
  // Рубеж 1: сырой HTML в разметку не превращается.
  html: false,
  // Ссылку делает ссылкой только явная запись markdown.
  linkify: false,
  // Типографские замены меняют текст пользователя — не применяем.
  typographer: false,
  breaks: true,
});

/**
 * Markdown → безопасный HTML для `v-html`.
 *
 * Пустой или пробельный текст даёт пустую строку: вызывающая сторона решает,
 * показывать ли заглушку.
 */
export function renderMarkdown(markdown: string): string {
  if (markdown.trim().length === 0) return "";
  return sanitizeHtml(renderer.render(markdown));
}

/**
 * Markdown одной строкой — для превью в списках и таблицах.
 *
 * Внутристрочная разметка разбирается, блочная (заголовки, списки) — нет,
 * поэтому вывод не ломает вёрстку ячейки.
 */
export function renderMarkdownInline(markdown: string): string {
  if (markdown.trim().length === 0) return "";
  return sanitizeHtml(renderer.renderInline(markdown));
}

/** Есть ли в заметке видимое содержимое. */
export function hasMarkdownContent(markdown: string): boolean {
  return markdown.trim().length > 0;
}
